
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from '../../components/UI';
import { ChannelType } from '../../types';
import { BrandLogo } from '../../components/BrandLogo';
import { Settings, RefreshCw, Check, ArrowRight, ListOrdered, ChevronUp, ChevronDown, X } from '../../components/Icons';
import { topologyData } from '../../data/topology';
import { useTranslation } from '../../contexts/LanguageContext';

const ChannelSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [channels] = useState([
    { id: 'ebill', type: ChannelType.EBILL, name: 'eBill Network', endpoint: 'api.ebill.ch/v2/gateway', path: '/settings/channels/ebill' },
    { id: 'edi', type: ChannelType.EDI, name: 'EDI Interface', endpoint: 'edi.io-market.com/standard', path: '/settings/channels/edi' },
    { id: 'email', type: ChannelType.EMAIL, name: 'eMail Gateway', endpoint: 'smtp.io-market.com:587', path: '/settings/channels/email' },
    { id: 'print', type: ChannelType.PRINT, name: 'Physical Print', endpoint: 'print-hub.local/spooler', path: '/settings/channels/print' },
  ]);

  const goBack = () => navigate('/dashboard');

  return (
    <div className="p-6 space-y-8 max-w-[1200px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('channel_configuration')} 
        breadcrumbs={[{ label: t('settings') }, { label: t('channel_settings') }]} 
        onBack={goBack}
      />

      <div className="grid grid-cols-1 gap-4">
        {channels.map((channel, index) => {
          const statusEntry = topologyData.connectivity.find(c => c.type === channel.type);
          const isActive = statusEntry?.isActive ?? false;

          return (
            <Card 
              key={channel.id} 
              className={`p-6 transition-all border border-gray-100 dark:border-[#393939] group cursor-pointer hover:shadow-md hover:border-brand-medium/20 ${isActive ? 'bg-white dark:bg-[#161616]' : 'bg-gray-50/30 dark:bg-white/5'}`}
              onClick={() => navigate(channel.path)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-4">
                     <span className="text-[10px] font-black text-gray-300 dark:text-gray-600 w-4">{index + 1}</span>
                     <div className={`transition-all duration-500 ${!isActive ? 'grayscale' : ''}`}>
                       <BrandLogo type={channel.type} size={48} />
                     </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-brand-dark dark:text-white tracking-tight leading-none">{channel.name}</h3>
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${
                        isActive 
                          ? 'bg-green-50 text-green-600 border-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30' 
                          : 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-white/5 dark:border-white/10'
                      }`}>
                        {isActive ? t('live') : t('inactive')}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.15em] mt-2">
                      {`${t('endpoint')}: ${channel.endpoint}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <div className="flex flex-col items-center gap-1.5">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        isActive 
                          ? 'bg-green-50 border-green-200 text-green-600 dark:bg-green-900/10 dark:border-green-900/30 dark:text-green-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-300 dark:bg-white/5 dark:border-white/10 dark:text-gray-500'
                      }`}>
                        <Check size={18} strokeWidth={4} />
                      </div>
                      <span className={`text-[9px] font-black uppercase tracking-widest ${isActive ? 'text-green-600 dark:text-green-500' : 'text-gray-400'}`}>
                        {isActive ? t('active') : t('paused')}
                      </span>
                  </div>

                  <ArrowRight size={20} className="text-gray-300 dark:text-gray-600 group-hover:text-brand-dark dark:group-hover:text-white transition-all transform group-hover:translate-x-1" />
                </div>
              </div>
            </Card>
          );
        })}

        {/* Change Priority Card */}
        <Card 
            className="p-6 transition-all border border-dashed border-gray-300 dark:border-gray-700 bg-transparent hover:border-brand-medium dark:hover:border-brand-medium hover:bg-brand-light/5 dark:hover:bg-brand-medium/10 cursor-pointer group flex items-center justify-center min-h-[100px]"
            onClick={() => navigate('/settings/channels/priority')}
        >
            <div className="flex items-center gap-3 text-gray-400 group-hover:text-brand-dark dark:group-hover:text-white transition-colors">
                <ListOrdered size={24} />
                <span className="text-sm font-bold uppercase tracking-widest">{t('change_priority')}</span>
            </div>
        </Card>
      </div>

      <div className="pt-8">
        <Card className="p-10 bg-[#474D22] text-white border-none shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
            <div className="space-y-3">
              <h4 className="text-2xl font-bold tracking-tight">{t('expand_reach')}</h4>
              <p className="text-white/60 text-[13px] max-w-lg font-medium leading-relaxed uppercase tracking-wider">
                Integrate custom proprietary ERP formats with our specialized EDI translation layers.
              </p>
            </div>
            <Button variant="secondary" size="lg" className="!text-brand-dark !bg-brand-light shadow-2xl hover:!scale-105">
              {t('contact_engineer')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChannelSettings;
